import GIS from "../app";
import PointerInteraction from 'ol/interaction/Pointer';
import OlOverlay from 'ol/Overlay';
import {Feature, MapBrowserEvent} from "ol";
import Modify from 'ol/interaction/Modify';
import {LineString, Point, Polygon} from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import {InteractionOptions} from "../configuration/map.ts";
import {HoverCallback, VertexHoverCallback} from "../general/callback.ts";
import {HoverOptions, VertexHoverOptions} from "../general/options.ts";

class Hover extends PointerInteraction {
    private readonly GIS: GIS;
    private readonly showIcon: boolean;
    private callbackOptions: HoverOptions;
    private callback: HoverCallback | undefined;
    private questionMark: OlOverlay | undefined;
    private lastFeatureId: string | number | null = null;
    private lastCheck = 0;

    //Vertex-hover state. Shares the throttle clock with feature-hover above.
    private vertexCallbackOptions: VertexHoverOptions = {layers: []};
    private vertexCallback: VertexHoverCallback | undefined;
    private lastVertexKey: string | null = null;
    private lastVertexCheck = 0;

    constructor(options: InteractionOptions, GIS: GIS) {
        super({
            handleEvent: event => this.handleEvent(event)
        });
        this.GIS = GIS;
        this.showIcon = options.icon ?? true;
        this.callbackOptions = { layers: [] };
    }

    public setCallback(options?: HoverOptions, callback?: HoverCallback): void {
        this.callback = callback;
        if (options) {
            this.callbackOptions = options;
        }
    }

    /**
     * Register a callback fired when the cursor enters or leaves an existing
     * vertex of a feature in any of the configured layers, while a Modify
     * interaction is active on the map. The payload mirrors
     * {@link HoverCallback}'s — same `id` / `layerName` / `x` / `y` /
     * `coordinate` — plus `vertexIndex` (and `ringIndex` for polygons).
     */
    public setVertexHoverCallback(options?: VertexHoverOptions, callback?: VertexHoverCallback): void {
        this.vertexCallback = callback;
        if (options) {
            this.vertexCallbackOptions = options;
        }
        // Reset dedupe state so the next pointermove re-emits.
        this.lastVertexKey = null;
    }

    public handleEvent = (event: MapBrowserEvent<any>): boolean => {
        if (event.type !== 'pointermove' || event.dragging) return true;

        this.handleFeatureHover(event);
        this.handleVertexHover(event);

        return true;
    }

    private handleFeatureHover(event: MapBrowserEvent<any>): void {
        const layerConfigs = this.callbackOptions.layers;
        if (!layerConfigs.length || !this.callback) return;

        const throttle = this.callbackOptions.throttle ?? 50;
        const now = Date.now();
        if (now - this.lastCheck < throttle) return;
        this.lastCheck = now;

        const configuredLayerNames = layerConfigs.map(l => l.layerName);
        const hitTolerance = this.callbackOptions.hitTolerance ?? 8;

        let foundFeature: any = null;
        let foundLayerName: string | null = null;

        this.GIS.Map.olObject!.forEachFeatureAtPixel(
            event.pixel,
            (feature, layer) => {
                foundFeature = feature;
                foundLayerName = layer?.get('name') ?? null;
                return true;
            },
            {
                layerFilter: (layer) => configuredLayerNames.includes(layer.get('name')),
                hitTolerance
            }
        );

        if (foundFeature && foundLayerName) {
            const layerConfig = layerConfigs.find(l => l.layerName === foundLayerName);
            const attribute = layerConfig?.attribute ?? 'id';
            const id = foundFeature.get(attribute);
            if (this.showIcon) this.updateQuestionMark(event.coordinate);

            if (id !== this.lastFeatureId) {
                this.lastFeatureId = id;
                this.GIS.Map.olObject!.getViewport().style.cursor = 'pointer';
                this.callback?.({
                    id,
                    layerName: foundLayerName,
                    x: event.pixel[0],
                    y: event.pixel[1],
                    coordinate: event.coordinate
                });
            }
        } else if (this.lastFeatureId !== null) {
            this.lastFeatureId = null;
            if (this.showIcon) this.hideQuestionMark();
            this.GIS.Map.olObject!.getViewport().style.cursor = '';
            this.callback?.(null);
        }
    }

    private handleVertexHover(_event: MapBrowserEvent<any>): void {
        if (!this.vertexCallback) return;
        const layerConfigs = this.vertexCallbackOptions.layers;
        if (!layerConfigs.length) return;

        const throttle = this.vertexCallbackOptions.throttle ?? 50;
        const now = Date.now();
        if (now - this.lastVertexCheck < throttle) return;
        this.lastVertexCheck = now;

        // Find the active Modify interaction (if any) and pull the OL-managed
        // vertex feature out of its overlay. OL stamps the feature with an
        // `existing` flag — true means the pointer is snapped to a real
        // vertex (not an insertion point on an edge).
        const map = this.GIS.Map.olObject;
        if (!map) return;

        const modify = map.getInteractions().getArray()
            .find((i): i is Modify => i instanceof Modify && i.getActive());
        if (!modify) {
            this.emitVertexLeave();
            return;
        }

        const overlaySource = modify.getOverlay().getSource();
        const vertexFeature = overlaySource?.getFeatures()
            .find((f: Feature) => f.get('existing') === true);
        if (!vertexFeature) {
            this.emitVertexLeave();
            return;
        }

        const geom = vertexFeature.getGeometry();
        if (!(geom instanceof Point)) {
            this.emitVertexLeave();
            return;
        }
        const coordinate = geom.getCoordinates();

        const candidates = (vertexFeature.get('features') as Feature[] | undefined) ?? [];
        const match = this.findVertexMatch(coordinate, candidates, layerConfigs);
        if (!match) {
            this.emitVertexLeave();
            return;
        }

        const pixel = map.getPixelFromCoordinate(coordinate) ?? [0, 0];
        const key = `${match.layerName}:${match.id}:${match.ringIndex ?? 0}:${match.vertexIndex}`;
        if (key === this.lastVertexKey) return;
        this.lastVertexKey = key;

        this.vertexCallback({
            id: match.id,
            layerName: match.layerName,
            x: pixel[0],
            y: pixel[1],
            coordinate,
            vertexIndex: match.vertexIndex,
            ringIndex: match.ringIndex,
        });
    }

    private emitVertexLeave(): void {
        if (this.lastVertexKey === null) return;
        this.lastVertexKey = null;
        this.vertexCallback?.(null);
    }

    /**
     * Resolve a hovered vertex coordinate to (layer, feature id, vertex
     * index) by checking each candidate against the configured layers. The
     * vertex coordinate from OL's overlay is exact, so a tiny squared-distance
     * tolerance (1 pixel by default) is enough.
     */
    private findVertexMatch(
        coordinate: number[],
        candidates: Feature[],
        layerConfigs: HoverOptions['layers']
    ): { id: string | number; layerName: string; vertexIndex: number; ringIndex?: number } | null {
        const map = this.GIS.Map.olObject;
        if (!map) return null;

        const view = map.getView();
        const res = view.getResolution() ?? 1;
        const tolPx = this.vertexCallbackOptions.hitTolerance ?? 1;
        const tol = tolPx * res;
        const tol2 = tol * tol;

        // Build {source → {layerName, attribute}} once per call for O(1) lookup.
        // We use GIS.Layer.getLayer because it walks layer groups via the
        // name index — map.getLayers() only returns top-level layers and
        // misses anything nested.
        const sourcesByLayer = new Map<VectorSource, { layerName: string; attribute: string }>();
        for (const cfg of layerConfigs) {
            let layer: unknown;
            try {
                layer = this.GIS.Layer.getLayer(cfg.layerName);
            } catch {
                continue;
            }
            if (layer instanceof VectorLayer) {
                const src = layer.getSource();
                if (src) sourcesByLayer.set(src, {layerName: cfg.layerName, attribute: cfg.attribute ?? 'id'});
            }
        }
        if (sourcesByLayer.size === 0) return null;

        // Fallback layer info: when we can't pin the candidate feature to a
        // specific configured source (e.g. it was added directly via a
        // Modify Collection, or the layer is wrapped in a way `hasFeature`
        // misses), reuse the first configured layer's name + attribute so
        // the callback still fires.
        const fallback = layerConfigs[0];
        const fallbackInfo = {
            layerName: fallback.layerName,
            attribute: fallback.attribute ?? 'id',
        };

        for (const feature of candidates) {
            const geometry = feature.getGeometry();
            if (!geometry) continue;

            let resolved: { layerName: string; attribute: string } | undefined;
            for (const [src, info] of sourcesByLayer) {
                if (src.hasFeature(feature)) {
                    resolved = info;
                    break;
                }
            }
            // Fall back to the first configured layer so the callback fires
            // even when source membership can't be determined.
            if (!resolved) resolved = fallbackInfo;

            const id = feature.get(resolved.attribute);
            if (id === undefined || id === null) continue;

            const type = geometry.getType();
            if (type === 'Polygon') {
                const rings = (geometry as Polygon).getCoordinates();
                for (let r = 0; r < rings.length; r++) {
                    const ring = rings[r];
                    for (let i = 0; i < ring.length - 1; i++) {
                        if (squaredDistance(ring[i], coordinate) <= tol2) {
                            return {id, layerName: resolved.layerName, vertexIndex: i, ringIndex: r};
                        }
                    }
                }
            } else if (type === 'LineString') {
                const coords = (geometry as LineString).getCoordinates();
                for (let i = 0; i < coords.length; i++) {
                    if (squaredDistance(coords[i], coordinate) <= tol2) {
                        return {id, layerName: resolved.layerName, vertexIndex: i};
                    }
                }
            } else if (type === 'Point') {
                const coords = (geometry as Point).getCoordinates();
                if (squaredDistance(coords, coordinate) <= tol2) {
                    return {id, layerName: resolved.layerName, vertexIndex: 0};
                }
            }
        }
        return null;
    }

    private updateQuestionMark(coordinate: number[]): void {
        if (this.questionMark) {
            this.questionMark.setPosition(coordinate);
        } else {
            this.showQuestionMark(coordinate);
        }
    }

    private showQuestionMark(coordinate: number[]): void {
        const element = document.createElement('i');
        element.className = 'fas fa-question-circle cfm-hover fa-sm';
        element.style.pointerEvents = 'none';

        this.questionMark = new OlOverlay({
            position: coordinate,
            positioning: 'center-left',
            element,
            offset: [14, 0],
            stopEvent: false
        });

        this.GIS.Map.olObject!.addOverlay(this.questionMark);
    }

    private hideQuestionMark(): void {
        if (this.questionMark) {
            this.GIS.Map.olObject!.removeOverlay(this.questionMark);
            this.questionMark = undefined;
        }
    }
}

function squaredDistance(a: number[], b: number[]): number {
    const dx = a[0] - b[0], dy = a[1] - b[1];
    return dx * dx + dy * dy;
}

export default Hover;
